import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ModalViewer from '@components/Modal/ModalViewer';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import { CancelCaseModalInfo } from '@components/CancelCase/CancelCaseModalInfo/CancelCaseModalInfo';
import { CancelCaseModalActions } from '@components/CancelCase/CancelCaseModalActions/CancelCaseModalActions';
import { CancelCaseModalForm } from '@components/CancelCase/CancelCaseModalForm/CancelCaseModalForm';
import { CancelCaseModalProps, CancelCaseFormData, cancelCaseFormDefaultValues } from '@components/CancelCase/CancelCaseModalTypes';
import { Box } from '@mui-core-real';
import { CANCEL_CASE_MODAL } from '@components/Modal/modalConstants';

const useStyles = makeStyles((theme) =>
	createStyles({
		modalWrapper: {
			width: '90vw',
			maxWidth: '600px',
			maxHeight: '90vh',
			padding: theme.spacing(1.5),
			display: 'flex',
			flexDirection: 'column',
			backgroundColor: theme.palette.background.paper,
			boxShadow: theme.shadows[5],
			borderRadius: '2px',
		},
		form: {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			minHeight: 0,
			overflowY: 'auto',
		},
		modalContent: {
			display: 'flex',
			flexDirection: 'column',
			gap: theme.spacing(3),
			padding: theme.spacing(0, 1.5, 1.5, 1.5),
		},
	}),
);

export const CancelCaseModal: React.FC<CancelCaseModalProps> = ({
	onCaseCancel,
	caseInfo,
}) => {
	const classes = useStyles();
	const { closeModal } = useModalContext();
	const { surgeonName, hospitalName, unit, duration, surgeryDate, patientName } = caseInfo;

	const formMethods = useForm<CancelCaseFormData>({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		defaultValues: cancelCaseFormDefaultValues
	});

	const handleClose = () => {
		formMethods.reset();
		closeModal(CANCEL_CASE_MODAL.id);
	};

	const handleSubmit = (data: CancelCaseFormData) => {
		onCaseCancel(data);
		handleClose();
	};

	return (
		<ModalViewer
			id={CANCEL_CASE_MODAL.id}
			className={classes.modalWrapper}
			onClose={handleClose}
		>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(handleSubmit)} noValidate className={classes.form}>
					<Box className={classes.modalContent}>
						<CancelCaseModalInfo
							hospitalName={hospitalName}
							surgeonName={surgeonName}
							surgeryDate={surgeryDate}
							duration={duration}
							patientName={patientName}
							unit={unit}
						/>
						<CancelCaseModalForm />
						<CancelCaseModalActions
							onDismissClick={handleClose}
						/>
					</Box>
				</form>
			</FormProvider>
		</ModalViewer>
	);
};
